import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import { UserContext } from '../App';
import {
  customPageHtmlFileName,
  customPageMetadataFileName,
  deleteCustomPageFile,
  downloadCustomPageHtml,
  findCustomPageMetadata,
  normalizeCustomPagePath,
  uploadCustomPageFile,
} from '../util/customPages';

type CustomPageSourceType = 'content' | 'url';

type CustomPageForm = {
  title: string;
  path: string;
  content: string;
  iframeUrl: string;
  sourceType: CustomPageSourceType;
};

function CustomPageEditor() {
  const userCtx = useContext(UserContext);
  const { pageId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialPath = normalizeCustomPagePath(searchParams.get('path') ?? '');
  const [form, setForm] = useState<CustomPageForm>({
    title: '',
    path: initialPath,
    content: '',
    iframeUrl: '',
    sourceType: 'content',
  });
  const [original, setOriginal] = useState<CustomPageForm | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const baseline = useMemo(() => {
    if (original) {
      return original;
    }
    return {
      title: '',
      path: initialPath,
      content: '',
      iframeUrl: '',
      sourceType: 'content',
    } as CustomPageForm;
  }, [initialPath, original]);

  const isDirty = useMemo(() => {
    return (
      form.title !== baseline.title ||
      form.path !== baseline.path ||
      form.content !== baseline.content ||
      form.iframeUrl !== baseline.iframeUrl ||
      form.sourceType !== baseline.sourceType
    );
  }, [baseline, form]);

  useEffect(() => {
    if (!pageId) {
      setLoading(false);
      setOriginal(null);
      setForm({
        title: '',
        path: initialPath,
        content: '',
        iframeUrl: '',
        sourceType: 'content',
      });
      return;
    }

    setLoading(true);
    setError(null);

    findCustomPageMetadata(pageId, initialPath || undefined)
      .then(({ path, metadata }) => {
        const iframeUrl = (metadata.iframeUrl ?? '').trim();
        const contentPromise =
          iframeUrl.length > 0
            ? Promise.resolve('')
            : downloadCustomPageHtml(path, pageId).catch(() => '');

        return contentPromise.then((content) => ({
          path,
          metadata,
          content,
          iframeUrl,
        }));
      })
      .then(({ path, metadata, content }) => {
        const normalizedPath = normalizeCustomPagePath(path);
        const loaded = {
          title: metadata.title ?? '',
          path: normalizedPath,
          content: content ?? '',
          iframeUrl: metadata.iframeUrl ?? '',
          sourceType: metadata.iframeUrl ? ('url' as CustomPageSourceType) : ('content' as CustomPageSourceType),
        };
        setForm(loaded);
        setOriginal(loaded);
      })
      .catch((e: Error) => {
        console.error('Failed to load page data!', e);
        setError('Nepodarilo sa načítať stránku.');
      })
      .finally(() => setLoading(false));
  }, [initialPath, pageId]);

  const updateField = (field: keyof CustomPageForm, value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const updateSourceType = (sourceType: CustomPageSourceType) => {
    setForm((prev) => ({
      ...prev,
      sourceType,
    }));
  };

  const discardChanges = () => {
    setForm(baseline);
    setNotice(null);
    setError(null);
  };

  const slugify = (value: string): string => {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const generatePageId = (title: string): string => {
    const slug = slugify(title);
    const base = slug.length > 0 ? slug : 'page';
    return base + '-' + Date.now();
  };

  const saveChanges = () => {
    if (!userCtx.user) {
      setError('Pre uloženie stránky sa prihláste.');
      return;
    }

    const normalizedPath = normalizeCustomPagePath(form.path);
    if (!normalizedPath) {
      setError('Zadajte cestu pre stránku.');
      return;
    }

    const trimmedUrl = form.iframeUrl.trim();
    if (form.sourceType === 'url' && !trimmedUrl) {
      setError('Zadajte URL pre iframe.');
      return;
    }

    setSaving(true);
    setNotice(null);
    setError(null);

    const targetPageId = pageId ?? generatePageId(form.title);
    const metadataFile = new File(
      [
        JSON.stringify({
          title: form.title,
          iframeUrl: form.sourceType === 'url' ? trimmedUrl : undefined,
        }),
      ],
      customPageMetadataFileName(targetPageId),
      { type: 'application/json' }
    );
    const htmlFile = new File(
      [form.sourceType === 'content' ? form.content : ''],
      customPageHtmlFileName(targetPageId),
      {
      type: 'text/html',
      }
    );

    Promise.resolve()
      .then(() => uploadCustomPageFile(normalizedPath, metadataFile))
      .then(() => uploadCustomPageFile(normalizedPath, htmlFile))
      .then(() => {
        if (pageId && original?.path) {
          const previousPath = normalizeCustomPagePath(original.path);
          if (previousPath && previousPath !== normalizedPath) {
            return Promise.all([
              deleteCustomPageFile(previousPath, customPageMetadataFileName(pageId)),
              deleteCustomPageFile(previousPath, customPageHtmlFileName(pageId)),
            ]).catch((deleteError: Error) => {
              console.error('Failed to delete previous files!', deleteError);
            });
          }
        }
        return undefined;
      })
      .then(() => {
        const saved = {
          title: form.title,
          path: normalizedPath,
          content: form.sourceType === 'content' ? form.content : '',
          iframeUrl: form.sourceType === 'url' ? trimmedUrl : '',
          sourceType: form.sourceType,
        };
        setForm(saved);
        setOriginal(saved);
        setNotice('Uložené.');

        if (!pageId) {
          navigate(
            '/custom/edit/' +
              targetPageId +
              '?path=' +
              encodeURIComponent(normalizedPath),
            { replace: true }
          );
        }
      })
      .catch((e: Error) => {
        console.error('Failed to save page!', e);
        setError('Nepodarilo sa uložiť stránku.');
      })
      .finally(() => setSaving(false));
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <Header />

      <div className="p-6 max-w-4xl w-full flex-1 overflow-y-auto">
        <h1 className="text-2xl font-bold mb-4">
          {pageId ? 'Upraviť stránku' : 'Vytvoriť stránku'}
        </h1>

        <div className="space-y-4">
          <div>
            <label htmlFor="custom-title" className="input-label">Názov</label>
            <input
              id="custom-title"
              className="input"
              type="text"
              value={form.title}
              onChange={(e) => updateField('title', e.target.value)}
            />
          </div>

          <div>
            <label htmlFor="custom-path" className="input-label">Cesta</label>
            <input
              id="custom-path"
              className="input"
              type="text"
              placeholder="/slovak"
              value={form.path}
              onChange={(e) => updateField('path', e.target.value)}
            />
          </div>

          <div>
            <div className="mb-3 inline-flex overflow-hidden rounded-t-lg border border-slate-300 bg-slate-100">
              <button
                type="button"
                className={
                  form.sourceType === 'content'
                    ? 'border-r border-slate-300 bg-white px-4 py-2 font-semibold text-slate-900'
                    : 'border-r border-slate-300 bg-slate-100 px-4 py-2 text-slate-600 transition hover:bg-slate-200'
                }
                onClick={() => updateSourceType('content')}
              >
                Uložiť obsah
              </button>
              <button
                type="button"
                className={
                  form.sourceType === 'url'
                    ? 'bg-white px-4 py-2 font-semibold text-slate-900'
                    : 'bg-slate-100 px-4 py-2 text-slate-600 transition hover:bg-slate-200'
                }
                onClick={() => updateSourceType('url')}
              >
                Načítať URL
              </button>
            </div>
            {form.sourceType === 'url' && (
              <div className="mb-3">
                <label htmlFor="custom-iframe-url" className="input-label">
                  URL
                </label>
                <input
                  id="custom-iframe-url"
                  className="input"
                  type="url"
                  placeholder="https://bkuliha.github.io/game-01/"
                  value={form.iframeUrl}
                  onChange={(e) => updateField('iframeUrl', e.target.value)}
                />
              </div>
            )}
            {form.sourceType === 'content' && (
              <div className="mb-3">
                <label htmlFor="custom-content" className="input-label">
                  Obsah (HTML + CSS + JS)
                </label>
                <textarea
                  id="custom-content"
                  className="input"
                  rows={12}
                  value={form.content}
                  onChange={(e) => updateField('content', e.target.value)}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn-primary"
            disabled={saving || !isDirty}
            onClick={saveChanges}
          >
            {saving ? 'Ukladám...' : 'Uložiť'}
          </button>
          <button
            type="button"
            className="btn-secondary"
            disabled={saving || !isDirty}
            onClick={discardChanges}
          >
            Zrušiť zmeny
          </button>

          {loading && <div>Načítavam...</div>}
          {error && <div className="text-red-500">{error}</div>}
          {notice && <div className="text-green-600">{notice}</div>}
        </div>
      </div>
    </div>
  );
}

export default CustomPageEditor;
